import { RegistrationBase } from '../../../_common/model/registration.base';

export enum LookupCodes {
    Default = 'default',
}

export class Contact {
    nameInKanji: string;
    givenNameInKanji: string;
    nameInKatakana: string;
    givenNameInKatakana: string;

    zip: string;
    prefecture: string;
    city: string;
    blockAndHouseNr: string;
    buildingName: string;

    englishPrefecture: string;
    englishSublocalities: string;
    englishWard: string;

    userSelectedPrefecture: string;
    userSelectedCity: string;

    phone: string;
    cellPhone: string;

    agreesToConditions?: boolean;
    allowsContact?: boolean;
}
export namespace Contact {
    export function oneLineAddress(contact: Contact): string {
        return (
            (contact.zip ? `〒${contact.zip}` + ' ' : '') +
            (contact.prefecture ? contact.prefecture + ' ' : '') +
            (contact.city ? contact.city + ' ' : '') +
            (contact.blockAndHouseNr ? contact.blockAndHouseNr + ' ' : '') +
            (contact.buildingName ? contact.buildingName : '')
        );
    }

    export function oneLineBillingAddress(decision: Decision): string {
        return (
            (decision.billingZip ? `〒${decision.billingZip}` + ' ' : '') +
            (decision.billingPrefecture
                ? decision.billingPrefecture + ' '
                : '') +
            (decision.billingCity ? decision.billingCity + ' ' : '') +
            (decision.billingBlockAndHouseNr
                ? decision.billingBlockAndHouseNr + ' '
                : '') +
            (decision.billingBuildingName ? decision.billingBuildingName : '')
        );
    }
}

export class Product {
    electricity?: boolean;
    electricityMeterId?: string;
    electricityUsage?: number;
    electricityUsageUnit?: UsageUnits;
    electricityUsagePeriod?: UsagePeriods;
    electricityUsageMonth?: EnergyUsageMonth;
    electricityUsageProfile?: ElectricityUsageProfile;
    electricityUsageKwhPerYear?: number;
    electricityCurrentSupplier?: string;
    electricityOtherSupplierName?: string;
    electricityContractPlan: string;
    electricityContractCapacityIsSystemGenerated: boolean;
    electricityContractCapacity?: number;
    electricityContractCapacityUnit?: CapacityUnits;
    electricityCustomerContractNumber?: string;
    electricityContractHasExpiryDate?: HasExpiryDate;
    electricityContractExpiryDate?: Date;
    electricityHasSolarPanels?: boolean;
    electricityHasSwitchedSupplierBefore?: boolean;
    electricityBills: string;
    electricityIsAllDenka?: boolean;

    gas?: boolean;
    gasMeterId?: string;
    gasUsage?: number;
    gasUsageUnit?: UsageUnits;
    gasUsagePeriod?: UsagePeriods;
    gasUsageMonth?: EnergyUsageMonth;
    gasUsageProfile?: GasUsageProfile;
    gasHouseType?: HouseType;
    gasUsageM3PerYear?: number;
    gasCurrentSupplier?: string;
    gasOtherSupplierName?: string;
    gasCustomerContractNumber?: string;
    gasContractHasExpiryDate?: HasExpiryDate;
    gasContractExpiryDate?: Date;
    gasHasSwitchedSupplierBefore?: boolean;
    gasBills: string;

    percentageOfGreenEnergyInOffer: PercentageOfGreenEnergyInOffer;
    howDidYouHearAboutUs: string;
}

export class Proposal {
    electricitySupplierCode?: string;
    electricityMarketOfferYenPerYear?: number;
    electricityAuctionOfferYenPerYear?: number;
    electricityFirstYearDiscount?: number;

    gasSupplierCode?: string;
    gasMarketOfferYenPerYear?: number;
    gasAuctionOfferYenPerYear?: number;
    gasFirstYearDiscount?: number;

    switchWindowDate?: Date;
    switchWindowDaysAfterAuction?: number;
}

export class Decision {
    electricityOfferAccepted?: boolean;
    gasOfferAccepted?: boolean;

    hasDifferentBillingAddress?: boolean;
    billingZip?: string;
    billingPrefecture?: string;
    billingCity?: string;
    billingBlockAndHouseNr?: string;
    billingBuildingName?: string;

    paymentMethod?: PaymentMethod;
    billDelivery?: BillDelivery;
    dateOfBirth?: Date;
    desiredStartDate?: Date;
    gasDesiredStartDate?: Date;
    agreesToSupplierConditions?: boolean;
    agreesToOfficialExplanation?: boolean;
    agreesToAllDenkaPolicy?: boolean;
    agreesToCancelFee?: boolean;
    gasAgreesToCancelFee?: boolean;
    hasDifferentContractOwner?: boolean;
    hasAgreementWithContractOwnerToSwitch?: boolean;
    reasonToDecline?: string;
    reasonToDeclineDescription?: string;
    notesToSupplier?: string;
    contractorNameInKanji?: string;
    contractorGivenNameInKanji?: string;
    contractorNameInKatakana?: string;
    contractorGivenNameInKatakana?: string;
    gasHasDifferentContractOwner?: boolean;
    gasHasAgreementWithContractOwnerToSwitch?: boolean;
    gasContractorNameInKanji?: string;
    gasContractorGivenNameInKanji?: string;
    gasContractorNameInKatakana?: string;
    gasContractorGivenNameInKatakana?: string;
}

export class Registration extends RegistrationBase {
    contact: Contact;
    product: Product;
    proposal: Proposal;
    decision: Decision;

    constructor() {
        super();
        this.contact = new Contact();
        this.product = new Product();
        this.proposal = new Proposal();
        this.decision = new Decision();
    }
}

export class FileData {
    // Keep fields uppercase
    Name: string;
    Uri: string;
    ContentType: string;
}

export namespace Registration {
    export function isPersonalDataReady(registration: Registration): boolean {
        return (
            registration &&
            registration.contact !== null &&
            registration.contact.zip !== null &&
            registration.contact.zip !== undefined &&
            registration.email !== null &&
            registration.email !== undefined
        );
    }

    export function isProductDataReady(registration: Registration): boolean {
        return (
            registration &&
            registration.product !== null &&
            registration.product.electricity !== null &&
            registration.product.electricity !== undefined
        );
    }
}

export enum EnergyUsageMonth {
    January = 'January',
    February = 'February',
    March = 'March',
    April = 'April',
    May = 'May',
    June = 'June',
    July = 'July',
    August = 'August',
    September = 'September',
    October = 'October',
    November = 'November',
    December = 'December',
}
export namespace EnergyUsageMonth {
    export const values = [
        EnergyUsageMonth.January,
        EnergyUsageMonth.February,
        EnergyUsageMonth.March,
        EnergyUsageMonth.April,
        EnergyUsageMonth.May,
        EnergyUsageMonth.June,
        EnergyUsageMonth.July,
        EnergyUsageMonth.August,
        EnergyUsageMonth.September,
        EnergyUsageMonth.October,
        EnergyUsageMonth.November,
        EnergyUsageMonth.December,
    ];
    export function description(type: EnergyUsageMonth) {
        switch (type) {
            case EnergyUsageMonth.January:
                return 'month-january-label';
            case EnergyUsageMonth.February:
                return 'month-february-label';
            case EnergyUsageMonth.March:
                return 'month-march-label';
            case EnergyUsageMonth.April:
                return 'month-april-label';
            case EnergyUsageMonth.May:
                return 'month-may-label';
            case EnergyUsageMonth.June:
                return 'month-june-label';
            case EnergyUsageMonth.July:
                return 'month-july-label';
            case EnergyUsageMonth.August:
                return 'month-august-label';
            case EnergyUsageMonth.September:
                return 'month-september-label';
            case EnergyUsageMonth.October:
                return 'month-october-label';
            case EnergyUsageMonth.November:
                return 'month-november-label';
            case EnergyUsageMonth.December:
                return 'month-december-label';
            default:
                return 'unknown';
        }
    }
}

export enum ElectricityUsageProfile {
    OnePerson = 'OnePerson',
    TwoPersons = 'TwoPersons',
    ThreePersons = 'ThreePersons',
    FourPersons = 'FourPersons',
    FivePersons = 'FivePersons',
    SixPersons = 'SixPersons',
}
export namespace ElectricityUsageProfile {
    export function description(type: ElectricityUsageProfile) {
        switch (type) {
            case ElectricityUsageProfile.OnePerson:
                return 'elec-usage-1person-label';
            case ElectricityUsageProfile.TwoPersons:
                return 'elec-usage-2persons-label';
            case ElectricityUsageProfile.ThreePersons:
                return 'elec-usage-3persons-label';
            case ElectricityUsageProfile.FourPersons:
                return 'elec-usage-4persons-label';
            case ElectricityUsageProfile.FivePersons:
                return 'elec-usage-5persons-label';
            case ElectricityUsageProfile.SixPersons:
                return 'elec-usage-6persons-label';
            default:
                return 'unknown';
        }
    }
}

export enum GasUsageProfile {
    OnePerson = 'OnePerson',
    TwoPersons = 'TwoPersons',
    ThreePersons = 'ThreePersons',
    FourPersons = 'FourPersons',
}
export namespace GasUsageProfile {
    export function description(type: GasUsageProfile) {
        switch (type) {
            case GasUsageProfile.OnePerson:
                return 'gas-usage-1person-label';
            case GasUsageProfile.TwoPersons:
                return 'gas-usage-2persons-label';
            case GasUsageProfile.ThreePersons:
                return 'gas-usage-3persons-label';
            case GasUsageProfile.FourPersons:
                return 'gas-usage-4persons-label';
            default:
                return 'unknown';
        }
    }
}

export enum HouseType {
    Standalone = 'Standalone',
    Apartment = 'Apartment',
}
export namespace HouseType {
    export function description(type: HouseType) {
        switch (type) {
            case HouseType.Standalone:
                return 'gas-house-type-standalone';
            case HouseType.Apartment:
                return 'gas-house-type-apartment';
            default:
                return 'unknown';
        }
    }
}

export enum HasExpiryDate {
    Yes = 'Yes',
    No = 'No',
    DontKnow = 'DontKnow',
}

export enum UsageUnits {
    KWh = 'KWh',
    M3 = 'M3',
}

export enum CapacityUnits {
    Amps = 'Amps',
    kVA = 'kVA',
    kW = 'kW',
    Unrequired = 'Unrequired',
    Unknown = 'Unknown', // This value is not mapped into API
}

export namespace ElecCapacityUnits {
    export function description(type: CapacityUnits) {
        switch (type) {
            case CapacityUnits.Amps:
                return 'unit-amper';
            case CapacityUnits.kVA:
                return 'unit-kilovolt-ampere';
            case CapacityUnits.kW:
                return 'unit-kilowatt';
            case CapacityUnits.Unknown:
                return 'unknown';
            default:
                return 'unknown';
        }
    }
}

export enum UsagePeriods {
    Day = 'Day',
    Month = 'Month',
    Quarter = 'Quarter',
    Year = 'Year',
}

export enum PaymentMethod {
    Automatic = 'Automatic',
    Manual = 'Manual',
}

export enum BillDelivery {
    Email = 'Email',
    Post = 'Post',
    Agent = 'Agent',
}

export enum ReasonToDeclineOption {
    NoReason = 'NoReason',
    SavingTooSmall = 'SavingTooSmall',
    NoTrustInSupplier = 'NoTrustInSupplier',
    ReceivedBetterOffer = 'ReceivedBetterOffer',
    NoTrustInSwitchProcess = 'NoTrustInSwitchProcess',
    OtherReason = 'OtherReason',
}

export enum PercentageOfGreenEnergyInOffer {
    DontKnow = 'DontKnow',
    Percent30 = 'Percent30',
    Percent35 = 'Percent35',
    Percent100 = 'Percent100',
}

export namespace PercentageOfGreenEnergyOptions {
    export function description(input: PercentageOfGreenEnergyInOffer): string {
        switch (input) {
            case PercentageOfGreenEnergyInOffer.Percent30:
                return 'percentage-of-green-energy-in-offer-30';
            case PercentageOfGreenEnergyInOffer.Percent35:
                return 'percentage-of-green-energy-in-offer-35';
            case PercentageOfGreenEnergyInOffer.Percent100:
                return 'percentage-of-green-energy-in-offer-100';
            case PercentageOfGreenEnergyInOffer.DontKnow:
                return 'percentage-of-green-energy-in-offer-dontknow';
            default:
                return `missing-logic-for-${input}`;
        }
    }
}
